import { useState, useEffect, useRef } from "react"
import { useParams, Link } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Trash2,
  Pencil,
  Save,
  X,
  BookTemplate,
  GripVertical,
  Search,
} from "lucide-react"
import { getClients } from "../../api/clients"
import { getRoutines, createRoutine, updateRoutine, deleteRoutine, getTemplates } from "../../api/routines"
import { getExercises } from "../../api/exercises"
import type { Client, Routine, Exercise, RepsUnit, WeightUnit } from "../../types"

const DAY_OPTIONS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
]

function getDayLabel(day?: number): string {
  if (day === undefined || day === null) return "Sin día asignado"
  return DAY_OPTIONS.find((d) => d.value === day)?.label ?? "Sin día asignado"
}

const MUSCLE_COLORS: Record<string, string> = {
  legs: "bg-green-500/20 text-green-400 border-green-500/20",
  chest: "bg-red-500/20 text-red-400 border-red-500/20",
  back: "bg-blue-500/20 text-blue-400 border-blue-500/20",
  shoulders: "bg-purple-500/20 text-purple-400 border-purple-500/20",
  arms: "bg-orange-500/20 text-orange-400 border-orange-500/20",
  core: "bg-yellow-500/20 text-yellow-400 border-yellow-500/20",
}

function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/60 border-white/10"
}

const REPS_UNITS: { value: RepsUnit; label: string; short: string }[] = [
  { value: "reps",         label: "Reps",               short: "reps"    },
  { value: "reps_per_side",label: "Reps/lado",          short: "reps/lado"},
  { value: "rir",          label: "RIR",                short: "RIR"     },
  { value: "rpe",          label: "Car. esfuerzo (RPE)",short: "RPE"     },
]

const WEIGHT_UNITS: { value: WeightUnit; label: string }[] = [
  { value: "kg",         label: "Kg"  },
  { value: "lb",         label: "Lb"  },
  { value: "rm_percent", label: "%RM" },
  { value: "bw_percent", label: "%PC" },
]

interface PendingEx {
  id?: number
  exercise_id: number
  sets: number
  reps: number
  reps_unit: RepsUnit
  weight?: number
  weight_unit: WeightUnit
  position?: number
  _destroy?: boolean
}

function RoutineCard({
  routine,
  exercises,
  clientId,
  onDelete,
  onUpdate,
  onSaveAsTemplate,
}: {
  routine: Routine
  exercises: Exercise[]
  clientId: number
  onDelete: () => void
  onUpdate: (r: Routine) => void
  onSaveAsTemplate: (r: Routine) => Promise<void>
}) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [savedTemplate, setSavedTemplate] = useState(false)
  const [name, setName] = useState(routine.name)
  const [selectedDay, setSelectedDay] = useState<number | "">(routine.scheduled_day ?? "")
  const [startHour, setStartHour] = useState<number | "">(routine.start_hour ?? "")
  const [endHour, setEndHour]     = useState<number | "">(routine.end_hour ?? "")
  const [pending, setPending] = useState<PendingEx[]>(
    routine.routine_exercises.map((re) => ({
      id: re.id,
      exercise_id: re.exercise.id,
      sets: re.sets,
      reps: re.reps,
      reps_unit: re.reps_unit ?? "reps",
      weight: re.weight,
      weight_unit: re.weight_unit ?? "kg",
    }))
  )
  const [addExId, setAddExId] = useState<number>(exercises[0]?.id ?? 0)
  const [addSets, setAddSets] = useState(3)
  const [addReps, setAddReps] = useState(10)
  const [addRepsUnit, setAddRepsUnit] = useState<RepsUnit>("reps")
  const [addWeight, setAddWeight] = useState("")
  const [addWeightUnit, setAddWeightUnit] = useState<WeightUnit>("kg")
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)
  const [exSearch, setExSearch] = useState("")
  const [showExPicker, setShowExPicker] = useState(false)
  const exPickerRef = useRef<HTMLDivElement>(null)

  const visible = pending.filter((p) => !p._destroy)
  const exerciseMap = Object.fromEntries(exercises.map((e) => [e.id, e]))

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (exPickerRef.current && !exPickerRef.current.contains(e.target as Node)) {
        setShowExPicker(false)
        setExSearch("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  function discardEdits() {
    setName(routine.name)
    setSelectedDay(routine.scheduled_day ?? "")
    setStartHour(routine.start_hour ?? "")
    setEndHour(routine.end_hour ?? "")
    setPending(
      routine.routine_exercises.map((re) => ({
        id: re.id,
        exercise_id: re.exercise.id,
        sets: re.sets,
        reps: re.reps,
        reps_unit: re.reps_unit ?? "reps",
        weight: re.weight,
        weight_unit: re.weight_unit ?? "kg",
      }))
    )
    setEditing(false)
  }

  function removeExercise(pendingIdx: number) {
    setPending((prev) =>
      prev[pendingIdx].id
        ? prev.map((p, i) => (i === pendingIdx ? { ...p, _destroy: true } : p))
        : prev.filter((_, i) => i !== pendingIdx)
    )
  }

  function addExercise() {
    if (!addExId) return
    setPending((prev) => [
      ...prev,
      {
        exercise_id: addExId,
        sets: addSets,
        reps: addReps,
        reps_unit: addRepsUnit,
        weight: addWeight ? Number(addWeight) : undefined,
        weight_unit: addWeightUnit,
      },
    ])
  }

  function moveExercise(fromIdx: number, toIdx: number) {
    const destroyed = pending.filter((p) => p._destroy)
    const reordered = [...visible]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    setPending([...reordered, ...destroyed])
  }

  async function handleSave() {
    setSaving(true)
    try {
      const updated = await updateRoutine(
        routine.id,
        {
          name,
          scheduled_day: selectedDay !== "" ? selectedDay : null,
          start_hour: startHour !== "" ? startHour : null,
          end_hour:   endHour   !== "" ? endHour   : null,
          routine_exercises_attributes: pending.map((p, idx) => ({
            ...(p.id ? { id: p.id } : {}),
            exercise_id: p.exercise_id,
            sets: p.sets,
            reps: p.reps,
            reps_unit: p.reps_unit,
            weight: p.weight,
            weight_unit: p.weight_unit,
            position: p._destroy ? p.position : idx,
            ...(p._destroy ? { _destroy: "1" } : {}),
          })),
        },
        clientId
      )
      onUpdate(updated)
      setPending(
        updated.routine_exercises.map((re) => ({
          id: re.id,
          exercise_id: re.exercise.id,
          sets: re.sets,
          reps: re.reps,
          reps_unit: re.reps_unit ?? "reps",
          weight: re.weight,
          weight_unit: re.weight_unit ?? "kg",
        }))
      )
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveAsTemplate() {
    setSavingTemplate(true)
    try {
      await onSaveAsTemplate(routine)
      setSavedTemplate(true)
      setTimeout(() => setSavedTemplate(false), 2000)
    } finally {
      setSavingTemplate(false)
    }
  }

  return (
    <div className="gradient-card border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors">
      {/* Card Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="p-2 rounded-lg bg-primary/10 shrink-0">
          <Dumbbell size={16} className="text-primary" />
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="flex-1 text-left min-w-0">
          <p className="font-semibold text-white truncate">{routine.name}</p>
          <p className="text-xs text-muted">
            {getDayLabel(routine.scheduled_day)}
            {routine.start_hour != null && routine.end_hour != null && (
              <span className="ml-1 text-primary/70">
                · {String(routine.start_hour).padStart(2,"0")}:00–{String(routine.end_hour).padStart(2,"0")}:00
              </span>
            )}
            {" "}· {visible.length} ejercicio{visible.length !== 1 ? "s" : ""}
          </p>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleSaveAsTemplate}
            disabled={savingTemplate}
            className={`p-1.5 rounded-lg transition ${
              savedTemplate
                ? "text-success bg-success/10"
                : "text-muted hover:text-amber-400 hover:bg-amber-400/10"
            }`}
            title="Guardar en Galería"
          >
            {savedTemplate ? <BookTemplate size={14} className="text-success" /> : <BookTemplate size={14} />}
          </button>
          <button
            onClick={() => { setExpanded(true); setEditing(true) }}
            className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-primary/10 transition"
            title="Editar"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-muted hover:text-danger hover:bg-danger/10 transition"
            title="Eliminar"
          >
            <Trash2 size={14} />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg text-muted hover:text-white transition"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Card Body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-4 space-y-4">
              {/* Edit Name/Date/Hours */}
              {editing && (
                <div className="space-y-2">
                  <div className="flex gap-3">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nombre del día"
                      className="flex-1 rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                    />
                    <select
                      value={selectedDay}
                      onChange={(e) => setSelectedDay(e.target.value === "" ? "" : Number(e.target.value))}
                      className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                    >
                      <option value="">Sin día</option>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  {/* Bloque horario */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted shrink-0">⏱ Horario:</span>
                    <select
                      value={startHour}
                      onChange={(e) => {
                        const val = e.target.value === "" ? "" : Number(e.target.value)
                        setStartHour(val)
                        // Auto-set end hour to start + 1
                        setEndHour(val !== "" ? val + 1 : "")
                      }}
                      className="rounded-lg border border-white/10 bg-surface-elevated px-2 py-1.5 text-sm text-white focus:border-primary"
                    >
                      <option value="">--</option>
                      {Array.from({ length: 15 }, (_, i) => i + 6).map((h) => (
                        <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>
                      ))}
                    </select>
                    <span className="text-xs text-muted">a</span>
                    <select
                      value={endHour}
                      onChange={(e) => setEndHour(e.target.value === "" ? "" : Number(e.target.value))}
                      disabled={startHour === ""}
                      className="rounded-lg border border-white/10 bg-surface-elevated px-2 py-1.5 text-sm text-white focus:border-primary disabled:opacity-50"
                    >
                      {startHour === "" ? (
                        <option value="">--</option>
                      ) : (
                        <option value={Number(startHour) + 1}>
                          {String(Number(startHour) + 1).padStart(2, "0")}:00
                        </option>
                      )}
                    </select>
                  </div>
                </div>
              )}

              {/* Exercise List */}
              {visible.length === 0 ? (
                <p className="text-sm text-muted italic">Sin ejercicios aún.</p>
              ) : (
                <div className="space-y-2">
                  {visible.map((p, idx) => {
                    const realIdx = pending.indexOf(p)
                    const ex = exerciseMap[p.exercise_id]
                    const isDraggingOver = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx
                    return (
                      <div
                        key={idx}
                        draggable={editing}
                        onDragStart={() => { setDragIdx(idx); setDragOverIdx(idx) }}
                        onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx) }}
                        onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveExercise(dragIdx, idx); setDragIdx(null); setDragOverIdx(null) }}
                        onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                        className={`flex items-center gap-3 rounded-lg bg-surface-elevated px-3 py-2.5 transition-colors ${
                          editing ? "cursor-grab active:cursor-grabbing" : ""
                        } ${isDraggingOver ? "border border-primary/50 bg-primary/5" : "border border-transparent"}`}
                      >
                        {editing && (
                          <GripVertical size={14} className="text-muted/40 shrink-0 pointer-events-none" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-white">{ex?.name ?? "Desconocido"}</span>
                            {ex && (
                              <span className={`rounded-full border px-2 py-0.5 text-xs ${getMuscleColor(ex.muscle_group)}`}>
                                {ex.muscle_group}
                              </span>
                            )}
                          </div>
                          {editing ? (
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-muted">Series</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={p.sets}
                                  onChange={(e) => setPending((prev) => prev.map((x, i) => i === realIdx ? { ...x, sets: Number(e.target.value) } : x))}
                                  className="w-12 rounded border border-white/10 bg-surface px-1.5 py-0.5 text-xs text-white outline-none focus:border-primary"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-muted">Reps</label>
                                <input
                                  type="number"
                                  min={1}
                                  value={p.reps}
                                  onChange={(e) => setPending((prev) => prev.map((x, i) => i === realIdx ? { ...x, reps: Number(e.target.value) } : x))}
                                  className="w-12 rounded border border-white/10 bg-surface px-1.5 py-0.5 text-xs text-white outline-none focus:border-primary"
                                />
                              </div>
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-muted">Peso</label>
                                <input
                                  type="number"
                                  min={0}
                                  step={0.5}
                                  value={p.weight ?? ""}
                                  onChange={(e) => setPending((prev) => prev.map((x, i) => i === realIdx ? { ...x, weight: e.target.value ? Number(e.target.value) : undefined } : x))}
                                  placeholder="—"
                                  className="w-14 rounded border border-white/10 bg-surface px-1.5 py-0.5 text-xs text-white outline-none focus:border-primary"
                                />
                                <select
                                  value={p.weight_unit}
                                  onChange={(e) => setPending((prev) => prev.map((x, i) => i === realIdx ? { ...x, weight_unit: e.target.value as WeightUnit } : x))}
                                  className="rounded border border-white/10 bg-surface px-1 py-0.5 text-xs text-white outline-none focus:border-primary"
                                >
                                  {WEIGHT_UNITS.map((u) => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-muted mt-0.5">
                              {p.sets} series × {p.reps} {REPS_UNITS.find(u => u.value === p.reps_unit)?.short ?? "reps"}
                              {p.weight ? ` · ${p.weight} ${WEIGHT_UNITS.find(u => u.value === p.weight_unit)?.label ?? "kg"}` : ""}
                            </p>
                          )}
                        </div>
                        {editing && (
                          <button
                            onClick={() => removeExercise(realIdx)}
                            className="text-muted hover:text-danger transition shrink-0"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Add Exercise */}
              {editing && (
                <div className="rounded-lg border border-white/10 bg-surface p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted uppercase tracking-wider">Agregar Ejercicio</p>
                  <div className="space-y-3">
                    {/* Ejercicio: full width */}
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted px-1">Ejercicio</label>
                      <div className="relative" ref={exPickerRef}>
                        <button
                          type="button"
                          onClick={() => setShowExPicker((v) => !v)}
                          className="w-full flex items-center justify-between rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white text-left"
                        >
                          <span className="truncate">
                            {exerciseMap[addExId]?.name ?? "Seleccionar ejercicio"}
                          </span>
                          <ChevronDown size={14} className="text-muted shrink-0 ml-2" />
                        </button>
                        {showExPicker && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border border-white/10 bg-surface-elevated shadow-xl flex flex-col" style={{ maxHeight: "280px" }}>
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 shrink-0">
                              <Search size={13} className="text-muted shrink-0" />
                              <input
                                autoFocus
                                type="text"
                                value={exSearch}
                                onChange={(e) => setExSearch(e.target.value)}
                                placeholder="Buscar ejercicio..."
                                className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-muted/50"
                              />
                              {exSearch && (
                                <button onClick={() => setExSearch("")} className="text-muted hover:text-white">
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                            <div className="overflow-y-auto flex-1">
                              {(() => {
                                const q = exSearch.toLowerCase()
                                const filtered = exercises.filter((e) =>
                                  e.name.toLowerCase().includes(q) || e.muscle_group.toLowerCase().includes(q)
                                )
                                const groups = filtered.reduce<Record<string, Exercise[]>>((acc, ex) => {
                                  const g = ex.muscle_group;
                                  (acc[g] = acc[g] ?? []).push(ex)
                                  return acc
                                }, {})
                                if (filtered.length === 0) return (
                                  <p className="px-3 py-4 text-xs text-muted text-center">Sin resultados</p>
                                )
                                return Object.entries(groups).map(([group, exs]) => (
                                  <div key={group}>
                                    <p className="px-3 py-1.5 text-xs font-semibold text-muted/60 uppercase tracking-wider bg-black/20 sticky top-0">
                                      {group}
                                    </p>
                                    {exs.map((ex) => (
                                      <button
                                        key={ex.id}
                                        type="button"
                                        onClick={() => { setAddExId(ex.id); setShowExPicker(false); setExSearch("") }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary/10 hover:text-primary transition ${ex.id === addExId ? "text-primary bg-primary/5" : "text-white"}`}
                                      >
                                        {ex.name}
                                      </button>
                                    ))}
                                  </div>
                                ))
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Series + Reps + Tipo en una fila */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted px-1">Series</label>
                        <input
                          type="number"
                          min={1}
                          value={addSets}
                          onChange={(e) => setAddSets(Number(e.target.value))}
                          className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted px-1">Reps</label>
                        <input
                          type="number"
                          min={1}
                          value={addReps}
                          onChange={(e) => setAddReps(Number(e.target.value))}
                          className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted px-1">Tipo</label>
                        <select
                          value={addRepsUnit}
                          onChange={(e) => setAddRepsUnit(e.target.value as RepsUnit)}
                          className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                        >
                          {REPS_UNITS.map((u) => (
                            <option key={u.value} value={u.value}>{u.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Peso + Unidad en una fila */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted px-1">Peso <span className="text-muted/50">(opcional)</span></label>
                        <input
                          type="number"
                          value={addWeight}
                          onChange={(e) => setAddWeight(e.target.value)}
                          placeholder="—"
                          className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-muted px-1">Unidad</label>
                        <select
                          value={addWeightUnit}
                          onChange={(e) => setAddWeightUnit(e.target.value as WeightUnit)}
                          className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                        >
                          {WEIGHT_UNITS.map((u) => (
                            <option key={u.value} value={u.value}>{u.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      onClick={addExercise}
                      className="w-full flex items-center justify-center gap-1 rounded-lg bg-primary/10 border border-primary/30 px-3 py-2 text-sm text-primary hover:bg-primary/20 transition"
                    >
                      <Plus size={14} />
                      Agregar Ejercicio
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Actions */}
              {editing && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-50"
                  >
                    <Save size={14} />
                    {saving ? "Guardando..." : "Guardar Cambios"}
                  </button>
                  <button
                    onClick={discardEdits}
                    className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-muted hover:text-white transition"
                  >
                    <X size={14} />
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ProgramEditor() {
  const { clientId } = useParams<{ clientId: string }>()
  const id = Number(clientId)

  const [client, setClient] = useState<Client | null>(null)
  const [routines, setRoutines] = useState<Routine[]>([])
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(true)
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState("")
  const [newDay, setNewDay] = useState<number | "">("")
  const [creating, setCreating] = useState(false)

  // Import from gallery
  const [showImport, setShowImport] = useState(false)
  const [templatesList, setTemplatesList] = useState<Routine[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [importing, setImporting] = useState(false)

  useEffect(() => {
    Promise.all([getClients(), getRoutines({ userId: id }), getExercises()])
      .then(([clients, rts, exs]) => {
        setClient(clients.find((c) => c.id === id) ?? null)
        setRoutines(rts)
        setExercises(exs)
      })
      .finally(() => setLoading(false))
  }, [id])

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const routine = await createRoutine(
        { name: newName, scheduled_day: newDay !== "" ? newDay : undefined },
        id
      )
      setRoutines((prev) => [routine, ...prev])
      setNewName("")
      setNewDay("")
      setShowNew(false)
    } finally {
      setCreating(false)
    }
  }

  async function openImportModal() {
    setShowImport(true)
    setLoadingTemplates(true)
    try {
      const tpls = await getTemplates()
      setTemplatesList(tpls)
    } finally {
      setLoadingTemplates(false)
    }
  }

  async function handleImport(template: Routine) {
    setImporting(true)
    try {
      const routine = await createRoutine(
        {
          name: template.name,
          description: template.description,
          scheduled_day: template.scheduled_day,
          routine_exercises_attributes: template.routine_exercises.map((re) => ({
            exercise_id: re.exercise.id,
            sets: re.sets,
            reps: re.reps,
            reps_unit: re.reps_unit,
            weight: re.weight,
            weight_unit: re.weight_unit,
          })),
        },
        id
      )
      setRoutines((prev) => [routine, ...prev])
      setShowImport(false)
    } finally {
      setImporting(false)
    }
  }

  async function handleDelete(routineId: number) {
    if (!confirm("¿Eliminar esta rutina y todos sus ejercicios?")) return
    await deleteRoutine(routineId, id)
    setRoutines((prev) => prev.filter((r) => r.id !== routineId))
  }

  async function handleSaveAsTemplate(routine: Routine) {
    await createRoutine({
      name: routine.name,
      description: routine.description,
      scheduled_day: routine.scheduled_day,
      is_template: true,
      routine_exercises_attributes: routine.routine_exercises.map((re) => ({
        exercise_id: re.exercise.id,
        sets: re.sets,
        reps: re.reps,
        reps_unit: re.reps_unit,
        weight: re.weight,
        weight_unit: re.weight_unit,
      })),
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Dumbbell size={40} className="text-primary animate-pulse-glow" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to="/dashboard/training"
          className="flex items-center gap-2 text-sm text-muted hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} />
          Volver
        </Link>
        <div className="flex-1 min-w-0">
          <h2
            className="text-sm font-semibold text-white uppercase tracking-widest"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {client?.name ?? `Cliente #${id}`}
          </h2>
          {client?.membership && (
            <p className="text-xs text-muted capitalize">
              {client.membership.plan} · {client.membership.status === "active" ? "Activo" : "Suspendido"}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={openImportModal}
            className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20 transition"
          >
            <BookTemplate size={16} />
            Importar Plantilla
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan"
          >
            <Plus size={16} />
            Nueva Rutina
          </button>
        </div>
      </div>

      {/* Routine List */}
      {routines.length === 0 && !showNew ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="gradient-card border border-white/10 rounded-xl p-12 text-center"
        >
          <Dumbbell size={48} className="mx-auto mb-4 text-muted opacity-30" />
          <p className="text-muted text-sm">Sin rutinas para este cliente.</p>
          <p className="mt-1 text-xs text-muted/60">Haz clic en "Nueva Rutina" para comenzar.</p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {routines.map((r) => (
            <RoutineCard
              key={r.id}
              routine={r}
              exercises={exercises}
              clientId={id}
              onDelete={() => handleDelete(r.id)}
              onUpdate={(updated) =>
                setRoutines((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
              }
              onSaveAsTemplate={handleSaveAsTemplate}
            />
          ))}

          {/* New Routine Form (bottom) */}
          <AnimatePresence>
            {showNew && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="gradient-card border border-primary/30 rounded-xl p-5 space-y-3"
              >
                <h3 className="text-sm font-semibold text-white">Nueva Rutina</h3>
                <div className="flex gap-3">
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    placeholder="Ej: Día de Piernas"
                    autoFocus
                    className="flex-1 rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                  />
                  <select
                    value={newDay}
                    onChange={(e) => setNewDay(e.target.value === "" ? "" : Number(e.target.value))}
                    className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
                  >
                    <option value="">Sin día</option>
                    {DAY_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreate}
                    disabled={creating}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
                  >
                    <Save size={14} />
                    {creating ? "Creando..." : "Crear Rutina"}
                  </button>
                  <button
                    onClick={() => { setNewName(""); setNewDay(""); setShowNew(false) }}
                    className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-muted hover:text-white transition"
                  >
                    <X size={14} />
                    Cancelar
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Quick-add button (shown when form is closed) */}
          {!showNew && (
            <button
              onClick={() => setShowNew(true)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 py-3 text-sm text-muted hover:border-primary/40 hover:text-primary transition"
            >
              <Plus size={16} />
              Agregar Rutina
            </button>
          )}
        </div>
      )}

      {/* Import Template Modal */}
      <AnimatePresence>
        {showImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowImport(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="gradient-card border border-white/20 rounded-2xl p-6 max-w-lg w-full space-y-4 max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Importar Plantilla</h3>
                <button
                  onClick={() => setShowImport(false)}
                  className="p-2 rounded-lg text-muted hover:text-white hover:bg-white/10 transition"
                >
                  <X size={18} />
                </button>
              </div>

              {loadingTemplates ? (
                <div className="flex items-center justify-center py-8">
                  <Dumbbell size={24} className="text-primary animate-pulse-glow" />
                </div>
              ) : templatesList.length === 0 ? (
                <div className="text-center py-8">
                  <BookTemplate size={32} className="mx-auto mb-3 text-muted opacity-30" />
                  <p className="text-sm text-muted">No hay plantillas creadas.</p>
                  <p className="text-xs text-muted/60 mt-1">
                    Crea plantillas en la Galería de Rutinas.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 overflow-y-auto flex-1">
                  {templatesList.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => handleImport(tpl)}
                      disabled={importing}
                      className="w-full text-left rounded-lg border border-white/10 bg-surface-elevated p-4 hover:border-primary/30 hover:bg-primary/5 transition disabled:opacity-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                          <BookTemplate size={14} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-white truncate">{tpl.name}</p>
                          <p className="text-xs text-muted">
                            {getDayLabel(tpl.scheduled_day)} · {tpl.routine_exercises.length} ejercicio{tpl.routine_exercises.length !== 1 ? "s" : ""}
                          </p>
                          {tpl.routine_exercises.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {tpl.routine_exercises.slice(0, 4).map((re) => (
                                <span key={re.id} className="text-xs text-muted/70">
                                  {re.exercise.name}{re.sets ? ` ${re.sets}x${re.reps}` : ""}
                                </span>
                              ))}
                              {tpl.routine_exercises.length > 4 && (
                                <span className="text-xs text-muted/50">+{tpl.routine_exercises.length - 4} más</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
